import type {
  AlertView,
  ApplicationDetailView,
  ApplicationSummary,
  CorrelationView,
  CostView,
  ErrorGroupDetail,
  ErrorListView,
  EventLogView,
  HealthDetailView,
  HealthView,
  LogSearchView,
  MapView,
  Me,
  OverviewView,
  ProviderSummary,
  QueryView,
  DataStoreDetail, DataView,
  BackupView,
  QuotaView,
  ReportView,
  SearchResult,
  UsageView,
} from '@monitor/contracts';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, NotSignedIn, query } from './client.js';

// Query side of the console: one hook per view, each backed by one API query model.
// Live data refreshes every 30 s while the tab is visible (React Query pauses in background).

const LIVE = 30_000;

// The last profile is kept in the browser: at reopening the console shows at once and /me confirms
// it in the background. A 401 still sends the user to Security (the cookie is the real proof).
const ME_KEY = 'monitor.me';

function cachedMe(): Me | undefined {
  try {
    const text = localStorage.getItem(ME_KEY);
    return text ? (JSON.parse(text) as Me) : undefined;
  } catch {
    return undefined;
  }
}

export function forgetMe() {
  try {
    localStorage.removeItem(ME_KEY);
  } catch {
    // Storage not available: nothing to forget.
  }
}

async function loadMe(): Promise<Me> {
  try {
    const me = await api<Me>('/me');
    try {
      localStorage.setItem(ME_KEY, JSON.stringify(me));
    } catch {
      // Storage not available: the next opening waits for /me.
    }
    return me;
  } catch (error) {
    if (error instanceof NotSignedIn) forgetMe();
    throw error;
  }
}

export const useMe = () => useQuery({ queryKey: ['me'], queryFn: loadMe, staleTime: 5 * 60_000, initialData: cachedMe, initialDataUpdatedAt: 0 });

export const useMap = () => useQuery({ queryKey: ['map'], queryFn: () => api<MapView>('/map'), staleTime: 5 * 60_000, refetchInterval: 10 * 60_000, retry: 1 });

export const useOverview = () => useQuery({ queryKey: ['overview'], queryFn: () => api<OverviewView>('/overview'), refetchInterval: LIVE });

export const useCost = (days: number) =>
  useQuery({ queryKey: ['cost', days], queryFn: () => api<CostView>(`/cost${query({ days })}`), refetchInterval: 5 * 60_000, placeholderData: keepPreviousData });

export const useQuota = () => useQuery({ queryKey: ['quota'], queryFn: () => api<QuotaView>('/quota'), refetchInterval: 10 * 60_000 });

export const useData = () => useQuery({ queryKey: ['data'], queryFn: () => api<DataView>('/data'), refetchInterval: 10 * 60_000 });

export const useDataStore = (id: string) => useQuery({ queryKey: ['data', 'store', id], queryFn: () => api<DataStoreDetail>(`/data/store?id=${encodeURIComponent(id)}`), refetchInterval: 10 * 60_000 });

export const useBackup = () => useQuery({ queryKey: ['backup'], queryFn: () => api<BackupView>('/backup'), refetchInterval: 60_000 });

export const useQueryAnalysis = () => useQuery({ queryKey: ['query'], queryFn: () => api<QueryView>('/query'), refetchInterval: 10 * 60_000 });

export const useApplicationList = () => useQuery({ queryKey: ['application'], queryFn: () => api<ApplicationSummary[]>('/application'), refetchInterval: LIVE });

export const useApplication = (id: string) =>
  useQuery({ queryKey: ['application', id], queryFn: () => api<ApplicationDetailView>(`/application/${encodeURIComponent(id)}`), refetchInterval: LIVE });

export const useRemoveApplication = () =>
  useCommand<string>((id) => ({ path: `/application/${encodeURIComponent(id)}`, method: 'DELETE' }));

export const useErrorList = (filter: { application?: string; environment?: string; classification?: string; text?: string; sort?: string }) =>
  useQuery({ queryKey: ['error', filter], queryFn: () => api<ErrorListView>(`/error${query(filter)}`), refetchInterval: LIVE, placeholderData: keepPreviousData });

export const useError = (id: string) => useQuery({ queryKey: ['error', id], queryFn: () => api<ErrorGroupDetail>(`/error/${id}`), refetchInterval: LIVE });

export const useLog = (filter: Record<string, string | number | undefined>) =>
  useQuery({ queryKey: ['log', filter], queryFn: () => api<LogSearchView>(`/log${query(filter)}`), placeholderData: keepPreviousData });

export const useCorrelation = (id: string | null) =>
  useQuery({ queryKey: ['correlation', id], queryFn: () => api<CorrelationView>(`/correlation/${encodeURIComponent(id!)}`), enabled: Boolean(id) });

export const useHealth = () => useQuery({ queryKey: ['health'], queryFn: () => api<HealthView>('/health'), refetchInterval: LIVE });

export const useHealthDetail = (id: string | null) =>
  useQuery({ queryKey: ['health', id], queryFn: () => api<HealthDetailView>(`/health/${id}`), enabled: Boolean(id), refetchInterval: LIVE });

export const useUsage = (filter: { metric: string; groupBy: string; days: number; environment?: string }) =>
  useQuery({ queryKey: ['usage', filter], queryFn: () => api<UsageView>(`/usage${query(filter)}`), placeholderData: keepPreviousData });

export const useAlert = () => useQuery({ queryKey: ['alert'], queryFn: () => api<AlertView>('/alert'), refetchInterval: LIVE });

export const useReport = () => useQuery({ queryKey: ['segnalazione'], queryFn: () => api<ReportView>('/segnalazione'), refetchInterval: 15_000 });

export const useProvider = () => useQuery({ queryKey: ['provider'], queryFn: () => api<ProviderSummary[]>('/provider'), refetchInterval: 60_000 });

export const useEventLog = (enabled: boolean) => useQuery({ queryKey: ['event'], queryFn: () => api<EventLogView>('/event?limit=60'), enabled });

export const useSearch = (text: string) =>
  useQuery({ queryKey: ['search', text], queryFn: () => api<SearchResult[]>(`/search${query({ q: text })}`), enabled: text.trim().length >= 2, staleTime: 10_000 });

/** Commands: POST to the write side, then refresh every view that reads the same projections. */
export function useCommand<TInput>(build: (input: TInput) => { path: string; method?: string; json?: unknown }) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: TInput) => {
      const { path, method = 'POST', json } = build(input);
      return api<unknown>(path, { method, json: json ?? {} });
    },
    onSuccess: () => client.invalidateQueries(),
  });
}
